import SearchComponent from '@components/inputs/search/Search.component';
import ConfirmModal from '@components/modal/Confirm.modal';
import NewTableComponent from '@components/table/NewTable.component';
import TabsComponent from '@components/tabs/Tabs.component';
import { Colors } from '@constants/colors.constants';
import { GroupAction } from '@icons/index';
import { Add } from '@mui/icons-material';
import { Box, Button, Stack, styled, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { GridValueGetterParams } from '@mui/x-data-grid-pro';
import { useEffectFn } from '@ngneat/effects-hooks';
import { emitOnce } from '@ngneat/elf';
import { useObservable } from '@ngneat/react-rxjs';
import AuditGroupedActionModal, { AuditGroupedActionEnum } from '@screens/admin/audits/modal/AuditGroupedAction.modal';
import { searchAuditsEffect } from '@store/audits/audits.effects';
import { Audit, AuditsStatus, AuditsStatusTab } from '@store/audits/audits.model';
import { auditsQuery } from '@store/audits/audits.query';
import { auditsService } from '@store/audits/audits.service';
import { templatesQuery } from '@store/audits/templates/templates.query';
import { templatesService } from '@store/audits/templates/templates.service';
import { UserLight } from '@store/settings/users';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import { ConfirmActionProps, OtherActionType } from '@utils/table/columnTypes/actions.columnType';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';
import { finalize, Observable } from 'rxjs';
import MergeModal from './modal/MergeModal';
import NewAuditModal from './modal/NewAuditModal';
import NewTemplate from './modal/NewTemplate';
import TemplatesComponent from './template/templates';

interface StyledProps {
  isparticipant?: string;
}

export const CircleIcon = styled(Stack)<StyledProps>((props) => ({
  borderRadius: '50px',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: props.isparticipant === 'true' ? '#AEAEB2' : '#F4F6F8',
  padding: '6px',
  width: '20px',
  height: '20px',
}));

export const LetterText = styled(Stack)<StyledProps>((props) => ({
  fontWeight: 600,
  fontSize: '9px',
  letterSpacing: 0.5,
  color: props.isparticipant === 'true' ? Colors.white : Colors.black,
}));

export interface Audits {
  id: number;
  auditName: string;
  status: string;
  companyName: string;
  participants: string;
  updatedAt: Date;
  users?: UserLight[];
  author: UserLight;
}

const AuditsScreen = () => {
  const { t } = useTranslation();
  const [isNewAudit, setIsNewAudit] = useState(false);
  const [isNewTemplate, setIsNewTemplate] = useState(false);
  const [isMerged, setIsMerged] = useState(false);
  const [openConfirmAction, setOpenConfirmAction] = useState<ConfirmActionProps | false>(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [auditSelected, setAuditSelected] = useState<Audit>();

  const [openGroupedAction, setOpenGroupedAction] = useState<boolean>(false);
  const navigate = useNavigate();


  const [selectedAudits] = useObservable(auditsQuery.selectedAudits$);
  const [selectedTemplates] = useObservable(templatesQuery.selectedTemplates$);

  const searchAudits = useEffectFn(searchAuditsEffect);

  const [{ audits }] = useObservable(auditsQuery.audits$);

  const { enqueueSnackbar } = useSnackbar();

  const [filters] = useObservable(auditsQuery.filters$);
  const [sortModel] = useObservable(auditsQuery.sort$);
  const [tablePagination] = useObservable(auditsQuery.pagination$);

  useEffect(() => {
    searchAudits({ filters, tablePagination, sort: sortModel });
  }, [filters, searchAudits, sortModel, tablePagination]);

  const statusOrder = [
    AuditsStatus.PREAUDIT,
    AuditsStatus.AUDIT,
    AuditsStatus.RECOMMENDATION,
    AuditsStatus.OFFER,
    AuditsStatus.FINISHED,
  ];

  function getStatusBackgroundColor(status, index) {
    if (index < statusOrder.indexOf(status)) {
      return '#91E393';
    } else if (index === statusOrder.indexOf(status)) {
      return '#F2A93B';
    } else {
      return '';
    }
  }

  function groupedActionEnable(): boolean {
    if (tabIndex < 3 && selectedAudits.length > 0)
      return false;
    else return !(tabIndex === 3 && selectedTemplates.length > 0);
  }

  const handleChangeTabs = (value: number) => {
    setTabIndex(value);
    switch (value) {
      case 0:
        auditsService.setFilters('status')(AuditsStatusTab.ALL);
        break;
      case 1:
        auditsService.setFilters('status')(AuditsStatusTab.PENDING);
        break;
      case 2:
        auditsService.setFilters('status')(AuditsStatusTab.FINISHED);
        break;
    }
  };

  const handleDelete = (params: any, type?: string) => {
    setOpenConfirmAction({
      type: type === 'TEMPLATE' ? OtherActionType.TEMPLATE : OtherActionType.DELETE,
      row: params
    });
  };

  const handleMerge = (audit: Audit) => {
    setAuditSelected(audit);
    setIsMerged(true);
  };

  const handleTemplate = (params: any) => {
    setAuditSelected(params);
    setIsNewTemplate(true);
  };

  const handleConfirmValidation = (type: OtherActionType, audit: Audit) => (handleStopLoading: () => void) => {
    let validationObservable: Observable<any> | undefined = undefined;

    switch (type) {
      case OtherActionType.DELETE:
        validationObservable = auditsService.deleteAudit(audit.id);
        break;
      case OtherActionType.TEMPLATE:
        validationObservable = templatesService.deleteTemplate(audit.id);
        break;
    }
    validationObservable?.pipe(finalize(handleStopLoading)).subscribe({
      next: () => {
        enqueueSnackbar(`Votre ${type === OtherActionType.TEMPLATE ? 'template' : 'audit'} a été supprimé avec succès!`, { variant: 'success' });
        setOpenConfirmAction(false);
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };


  return (
    <Stack flex={1} width="100%" pt="34px" px="24px" spacing={2}>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <Typography fontSize="30px" fontWeight="700">
          {t('audits.title')}
        </Typography>
        <Stack alignSelf="flex-end" direction="row" alignItems="center" spacing={2}>
          <Button
            variant="outlined"
            color="primary"
            disabled={groupedActionEnable()}
            onClick={() => setOpenGroupedAction(true)}
            startIcon={<GroupAction />}
          >
            {t('global.groupedAction')}
          </Button>
          <Button variant="contained" color="primary" onClick={() => setIsNewAudit(true)} startIcon={<Add />}>
            {t('audits.addButton')}
          </Button>
        </Stack>
      </Stack>

      <TabsComponent
        ariaLabel="secondary"
        handleChange={handleChangeTabs}
        value={tabIndex}
        tabs={[t('routes.all'), t('routes.auditsInProcess'), t('routes.auditsDone'), t('routes.auditsTemplates')]}
      />
      <Stack flex={1} spacing={2} pb="25px">
        {tabIndex < 3 ? (
          <>
            <Stack direction="row" width={'100%'} justifyContent={'space-between'} spacing={2}>
              <Box width="500px">
                <SearchComponent
                  value={filters.search}
                  handleChange={(value) => {
                    emitOnce(() => {
                      auditsService.setFilters('search')(value);
                      auditsService.setPagination('page')(1);
                    });
                  }}
                />
              </Box>
              {/* <TableFiltersComponent
                filters={filters}
                handleSave={auditsService.setFilters('tableFilters')}
                handleReset={() => auditsService.resetFilters()} /> */}
            </Stack>
            <NewTableComponent
              columns={[
                COLUMN_TYPES.TEXT_BOLD({
                  field: 'name',
                  width: 250,
                  label: 'Audit',
                  sortable: true
                }),
                {
                  field: 'status',
                  width: 200,
                  headerName: 'Statut',
                  renderCell: (params) => {
                    const status = params.value;
                    return (
                      <Stack direction="row" spacing={0.5}>
                        {['PA', 'A', 'P', 'O'].map((title, index) => (
                          <CircleIcon
                            key={`k-${title}`}
                            sx={{ backgroundColor: getStatusBackgroundColor(status, index) }}
                          >
                            <LetterText>{title}</LetterText>
                          </CircleIcon>
                        ))}
                      </Stack>
                    );
                  },
                  sortable: true
                },
                COLUMN_TYPES.TEXT({
                  field: 'author',
                  width: 200,
                  color: 'secondary',
                  label: 'Author',
                  valueFormatter: (params) => `${params.value.firstName} ${params.value.lastName}`,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'clientOrganisationUnit',
                  color: 'secondary',
                  width: 350,
                  label: 'Entreprise',
                  sortable: true
                }),
                {
                  field: 'users',
                  width: 200,
                  headerName: 'Participants',
                  renderCell: (params) => {
                    const users = params.value;
                    return (
                      <Stack direction="row" spacing={0.5}>
                        {users.map((user) => (
                          <Tooltip title={`${user.firstName} ${user.lastName}`}>
                            <CircleIcon isparticipant="true" key={`k-${user.id}`}>
                              <LetterText isparticipant="true">
                                {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                              </LetterText>
                            </CircleIcon>
                          </Tooltip>
                        ))}
                      </Stack>
                    );
                  },
                },
                COLUMN_TYPES.TEXT({
                  field: 'updatedAt',
                  width: 200,
                  color: 'secondary',
                  label: 'Dernière modification',
                  valueGetter: (params: GridValueGetterParams<any, Audits>) => params.row.updatedAt,
                  valueFormatter: (params) =>
                    params.value ? t('global.dateAndHour', { date: new Date(params.value) }) : '',
                }),
                COLUMN_TYPES.ACTIONS({
                  width: 100,
                  handleShowRow: (audit: Audit) => navigate(`${audit.id}`),
                  actions: [
                    {
                      type: OtherActionType.DELETE,
                      handleClick: (params) => handleDelete(params),
                    },
                  ],
                  otherActions: [
                    {
                      type: OtherActionType.FUSION,
                      handleClick: (params: Audit) => handleMerge(params),
                      hidden: () => undefined,
                    },
                    {
                      type: OtherActionType.TEMPLATE,
                      handleClick: (params: Audit) => handleTemplate(params),
                      hidden: () => undefined,
                    },
                    {
                      type: OtherActionType.CSV,
                      handleClick: () => undefined,
                    },
                  ],
                }),
              ]}
              handlePageSizeChange={auditsService.setPagination('limit')}
              handlePageChange={(page) => auditsService.setPagination('page')(page + 1)}
              handleRowSelected={auditsService.setSelectedAudits}
              rowsSelected={selectedAudits}
              pageSize={tablePagination.limit}
              page={tablePagination.page - 1}
              handleSortModelChange={auditsService.setSort}
              totalRows={auditsQuery.totalRows}
              data={audits}
            />
          </>
        ) : (
          <TemplatesComponent />
        )}
        {!!openConfirmAction && (
          <ConfirmModal
            handleClose={() => setOpenConfirmAction(false)}
            description={t(`${OtherActionType.translationKey(openConfirmAction.type)}.description.audit`)}
            handleValidate={handleConfirmValidation(openConfirmAction.type, openConfirmAction.row)}
            title={t(`${OtherActionType.translationKey(openConfirmAction.type)}.title`)}
            validateButton={OtherActionType.label(openConfirmAction.type)}
          />
        )}
        {isNewAudit && (<NewAuditModal handleClose={() => setIsNewAudit(false)} />)}
        {isNewTemplate && (<NewTemplate handleClose={() => setIsNewTemplate(false)} auditId={auditSelected?.id} />)}
        {isMerged && (<MergeModal firstAudit={auditSelected!} handleClose={() => setIsMerged(false)} />)}
        {openGroupedAction && <AuditGroupedActionModal type={tabIndex < 3 ? AuditGroupedActionEnum.AUDIT : AuditGroupedActionEnum.TEMPLATE} handleClose={() => setOpenGroupedAction(false)} />}
        <Outlet />
      </Stack>
    </Stack >
  );
};

export default AuditsScreen;
